import { useEffect, useState } from "react";
import { FaEdit, FaHistory, FaPlay, FaSort, FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import { fetchWithAuth } from "../api/fetchWithAuth";
import TestCaseFormModal from "../components/TestCaseFormModal";
import TestCaseModal from "../components/TestCaseModal";
import TestCaseRunFormModal from "../components/TestCaseRunFormModal";
import TestCaseRunsModal from "../components/TestCaseRunsModal";

export default function TestCases({ user }) {
  const [testCases, setTestCases] = useState([]);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [runsTestCase, setRunsTestCase] = useState(null);
  const [runFormTestCase, setRunFormTestCase] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState("number");
  const [sortDir, setSortDir] = useState("asc");

  const roleCodes = user?.roles ?? [];
  const canManage = roleCodes.includes("PM") || roleCodes.includes("TST");

  useEffect(() => {
    loadTestCases();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  const loadTestCases = async () => {
    try {
      const res = await fetchWithAuth("/api/test-cases");

      if (!res.ok) {
        console.error("Failed to load test cases");
        return;
      }

      setTestCases(await res.json());
    } catch (err) {
      console.warn("loadTestCases aborted:", err.message);
    }
  };

  const openTestCase = async (id) => {
    const res = await fetchWithAuth(`/api/test-cases/${id}`);

    if (!res.ok) return;

    setSelectedTestCase(await res.json());
    setShowModal(true);
  };

  const deleteTestCase = async (id) => {
    if (!window.confirm("Delete this test case?")) return;

    const res = await fetchWithAuth(`/api/test-cases/${id}`, {
      method: "DELETE"
    });

    if (!res.ok) {
      const error = await res.json().catch(() => null);
      alert(error?.error || "Delete failed");
      return;
    }

    loadTestCases();
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const filteredTestCases = testCases.filter((testCase) => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) return true;

    return (
      String(testCase.number ?? "").toLowerCase().includes(query) ||
      String(testCase.name ?? "").toLowerCase().includes(query) ||
      String(testCase.type ?? "").toLowerCase().includes(query) ||
      String(testCase.description ?? "").toLowerCase().includes(query) ||
      String(testCase.expectedResult ?? "").toLowerCase().includes(query) ||
      String(testCase.User?.name ?? "").toLowerCase().includes(query)
    );
  });

  const sortedTestCases = [...filteredTestCases].sort((a, b) => {
    const direction = sortDir === "asc" ? 1 : -1;
    const valueA = String(a[sortField] ?? "").toLowerCase();
    const valueB = String(b[sortField] ?? "").toLowerCase();

    const compare = valueA.localeCompare(valueB, undefined, {
      numeric: true,
      sensitivity: "base"
    });

    if (compare !== 0) return compare * direction;

    return String(a.number ?? "").localeCompare(
      String(b.number ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  });

  const renderHighlightedText = (value) => {
    const text = String(value ?? "");
    const query = debouncedSearch.trim();

    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let startIndex = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) {
        parts.push(text.slice(startIndex, matchIndex));
      }

      const endIndex = matchIndex + query.length;
      parts.push(
        <mark key={`${text}-${matchIndex}`} className="px-0 bg-warning-subtle">
          {text.slice(matchIndex, endIndex)}
        </mark>
      );

      startIndex = endIndex;
      matchIndex = lowerText.indexOf(lowerQuery, startIndex);
    }

    if (startIndex < text.length) {
      parts.push(text.slice(startIndex));
    }

    return parts.length ? parts : text;
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <FaSort className="ms-1 text-muted" aria-hidden="true" />;
    }

    if (sortDir === "asc") {
      return <FaSortUp className="ms-1" aria-hidden="true" />;
    }

    return <FaSortDown className="ms-1" aria-hidden="true" />;
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
            <input
              type="text"
              className="form-control pe-5"
              placeholder="Search SW test specifications..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                }}
                aria-label="Clear search"
              >
                x
              </button>
            )}
          </div>

          <span className="badge text-bg-secondary">
            Total: {filteredTestCases.length}
          </span>
        </div>

        {canManage && (
          <button
            className="btn btn-success"
            onClick={() => {
              setEditingTestCase(null);
              setShowForm(true);
            }}
          >
            Add test case
          </button>
        )}
      </div>

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th
                style={{ ...stickyHeaderStyle, width: "150px", cursor: "pointer" }}
                onClick={() => handleSort("number")}
              >
                <span className="d-inline-flex align-items-center">
                  Number
                  {renderSortIndicator("number")}
                </span>
              </th>
              <th
                style={{ ...stickyHeaderStyle, minWidth: "220px", cursor: "pointer" }}
                onClick={() => handleSort("name")}
              >
                <span className="d-inline-flex align-items-center">
                  Name
                  {renderSortIndicator("name")}
                </span>
              </th>
              <th
                style={{ ...stickyHeaderStyle, width: "130px", cursor: "pointer" }}
                onClick={() => handleSort("type")}
              >
                <span className="d-inline-flex align-items-center">
                  Type
                  {renderSortIndicator("type")}
                </span>
              </th>
              <th style={{ ...stickyHeaderStyle, minWidth: "260px" }}>Description</th>
              <th style={{ ...stickyHeaderStyle, minWidth: "260px" }}>Expected result</th>
              <th style={{ ...stickyHeaderStyle, width: "140px" }}>User</th>
              <th style={{ ...stickyHeaderStyle, width: canManage ? "220px" : "80px" }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {sortedTestCases.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted py-4">
                  No SW test specifications found.
                </td>
              </tr>
            ) : sortedTestCases.map((testCase, index) => (
              <tr key={testCase.id} style={getRowStyle(index)}>
                <td>
                  <button
                    className="btn btn-link p-0 fw-bold"
                    onClick={() => openTestCase(testCase.id)}
                  >
                    {renderHighlightedText(testCase.number)}
                  </button>
                </td>
                <td>{renderHighlightedText(testCase.name)}</td>
                <td>{renderHighlightedText(testCase.type)}</td>
                <td>{renderHighlightedText(testCase.description)}</td>
                <td>{renderHighlightedText(testCase.expectedResult)}</td>
                <td>{renderHighlightedText(testCase.User?.name)}</td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-secondary me-2"
                    onClick={() => setRunsTestCase(testCase)}
                    title="Runs / Reports"
                  >
                    <FaHistory />
                  </button>

                  {canManage && (
                    <>
                      <button
                        className="btn btn-sm btn-outline-success me-2"
                        onClick={() => setRunFormTestCase(testCase)}
                        title="Add run"
                      >
                        <FaPlay />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          setEditingTestCase(testCase);
                          setShowForm(true);
                        }}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteTestCase(testCase.id)}
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TestCaseModal
        show={showModal}
        onClose={() => setShowModal(false)}
        testCase={selectedTestCase}
      />

      <TestCaseFormModal
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={loadTestCases}
        testCase={editingTestCase}
      />

      <TestCaseRunsModal
        show={Boolean(runsTestCase)}
        onClose={() => setRunsTestCase(null)}
        testCase={runsTestCase}
      />

      <TestCaseRunFormModal
        show={Boolean(runFormTestCase)}
        onClose={() => setRunFormTestCase(null)}
        onSaved={() => {}}
        testCase={runFormTestCase}
      />
    </div>
  );
}
